import type { PinMapItem } from '@/components/shared/map/PinMapEditor';
import { fallbackPinColor, PIN_UNASSIGNED_COLOR } from '@/components/shared/map/pinColors';
import type { TemplateChallengeGroup, TemplateChallengeLink } from '@/config/objects/templates';

/**
 * Build the map pins for a template's challenges. Each pin's location resolves
 * the override hierarchy: the per-template override wins, else the linked
 * library challenge's base location. Pins are colored by group.
 * See docs/CHALLENGE_LOCATION_HIERARCHY.md.
 */
export function buildTemplateMapItems(
  groups: TemplateChallengeGroup[],
  challenges: TemplateChallengeLink[],
): PinMapItem[] {
  // groupId → color/name (fallback palette by group order; ungrouped is gray).
  const groupMeta = new Map<string, { color: string; name: string }>();
  groups.forEach((group, index) => {
    groupMeta.set(group.id, { color: group.color || fallbackPinColor(index), name: group.name });
  });

  return challenges.map((link) => {
    const meta = link.groupId ? groupMeta.get(link.groupId) : undefined;
    return {
      kind: 'challenge' as const,
      id: link.id,
      name: link.libraryChallenge.name,
      // Resolved location: template override wins, else library base.
      latitude: link.latitude ?? link.libraryChallenge.latitude ?? null,
      longitude: link.longitude ?? link.libraryChallenge.longitude ?? null,
      color: meta?.color ?? PIN_UNASSIGNED_COLOR,
      groupName: meta?.name,
    };
  });
}
